// AdSense 보상형 광고 — `adsbygoogle` 을 부르는 유일한 파일.
//
// 공개 규격이 없어 구글 로더가 실제로 받는 모양을 따른다 (whif 가 2026-09-15 로더 원본을 읽어 확정한 계약 —
// 저장소 루트 frontend/src/ads/lib/adsense-provider.ts 머리 주석. 코드는 가져오지 않았다, 경계 규칙):
//   1. 큐에 { params: { google_ad_slot, google_ad_format: 'rewarded', google_ad_loaded_callback } } 를 넣는다
//   2. 콜백은 「광고를 받았다」는 신호다 — 본 것이 아니다. 못 받았으면 null 이 온다
//   3. 받은 컨트롤러의 show(cb) 를 불러야 화면에 뜬다
//   4. cb({ status }) — 'viewed' 일 때만 끝까지 본 것이다. 'closed'·'dismissed'·'error' 는 아니다
// 스크립트는 유저가 보상 버튼을 누른 뒤에만 붙인다 — 게이트가 한 번도 열리지 않은 화면에는 구글 태그가 아예 없다 (INV-006).
// 붙인 뒤에 게이트가 닫히면 태그는 남는다(이미 실행된 스크립트는 태그를 떼도 그대로다). 그때 막는 것은 show() 직전 재판정이다
import type { AdOutcome } from '../domain/reward-flow';

export const SCRIPT_ID = 'nnt-adsbygoogle';

export interface ShowOptions {
  /** 광고를 받은 뒤 띄우기 직전에 다시 묻는다 — 받는 사이 게이트가 닫혔으면 띄우지 않는다 (INV-006) */
  readonly allowed?: () => boolean;
  /** 광고 요청에 답이 없으면 「광고 없음」으로 친다. 구글 로더가 스스로 시간 제한을 걸지만, 차단기가 끼면 답이 영영 안 올 수 있다 */
  readonly loadGuardMs?: number;
  /** 광고가 떠 있는 동안은 기다린다. 끝내 답이 없으면 닫은 것으로 친다 */
  readonly watchGuardMs?: number;
}

// 두 상한 모두 버튼이 영영 잠기지 않게 하려는 것이다
const LOAD_GUARD_MS = 60_000;
const WATCH_GUARD_MS = 300_000;

interface RewardedAd {
  show(done: (result: { status?: string } | null | undefined) => void): void;
  /** 띄우지 않고 버린다 */
  disposeAd?(): void;
}

function discard(ad: RewardedAd | null): void {
  try {
    ad?.disposeAd?.();
  } catch {
    // silent-ok: 띄우지 않을 광고를 버리다 실패해도 할 일이 없다 — 화면에 뜬 것이 없다
  }
}

declare global {
  interface Window {
    adsbygoogle?: { push(item: unknown): void };
  }
}

let script: Promise<void> | null = null;

function loadScript(client: string): Promise<void> {
  script ??= new Promise<void>((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) return resolve();
    const el = document.createElement('script');
    el.id = SCRIPT_ID;
    el.async = true;
    el.crossOrigin = 'anonymous';
    el.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
    el.addEventListener('load', () => resolve());
    el.addEventListener('error', () => {
      // 실패를 기억하면 다음 시도도 바로 실패한다 — 걷어 내고 다음에 다시 붙인다
      script = null;
      el.remove();
      reject(new Error('adsbygoogle 스크립트를 받지 못했다'));
    });
    document.head.append(el);
  });
  return script;
}

function requestAd(slot: string, guardMs: number): Promise<RewardedAd | null> {
  return new Promise(resolve => {
    let waiting = true;
    const settle = (ad: RewardedAd | null) => {
      clearTimeout(guard);
      // 상한이 지난 뒤에 온 광고는 띄우지 않고 버린다 — 유저는 이미 「광고 없음」을 봤다
      if (!waiting) return discard(ad);
      waiting = false;
      resolve(ad);
    };
    const guard = setTimeout(() => settle(null), guardMs);
    try {
      const queue = (window.adsbygoogle ??= [] as unknown as { push(item: unknown): void });
      queue.push({
        params: {
          google_ad_slot: slot,
          google_ad_format: 'rewarded',
          google_ad_loaded_callback: (ad: RewardedAd | null) => settle(typeof ad?.show === 'function' ? ad : null),
        },
      });
    } catch {
      settle(null); // 로더가 슬롯·콜백 검사에서 던졌다
    }
  });
}

function present(ad: RewardedAd, guardMs: number): Promise<AdOutcome> {
  return new Promise(resolve => {
    const guard = setTimeout(() => resolve('dismissed'), guardMs);
    const settle = (outcome: AdOutcome) => {
      clearTimeout(guard);
      resolve(outcome); // 두 번째 resolve 는 무시된다 — 먼저 온 결말이 확정이다
    };
    try {
      ad.show(result => settle(result?.status === 'viewed' ? 'rewarded' : 'dismissed'));
    } catch {
      settle('unavailable');
    }
  });
}

/** 보상형 광고를 띄우고 결말을 돌려준다. 던지지 않는다 */
export async function showRewardedAd(client: string, slot: string, options: ShowOptions = {}): Promise<AdOutcome> {
  const allowed = options.allowed ?? (() => true);
  try {
    await loadScript(client);
  } catch {
    return 'unavailable'; // 광고 차단기·네트워크
  }
  if (!allowed()) return 'dismissed';
  const ad = await requestAd(slot, options.loadGuardMs ?? LOAD_GUARD_MS);
  if (!ad) return 'unavailable';
  if (!allowed()) {
    // 광고를 받는 사이 게이트가 닫혔다(성인 콘텐츠로 이동·결제 완료 등). 버튼은 이미 사라졌다 — 알릴 것 없이 버린다
    discard(ad);
    return 'dismissed';
  }
  return present(ad, options.watchGuardMs ?? WATCH_GUARD_MS);
}

/** 테스트 전용 — 스크립트를 붙였다는 기억을 지운다 */
export function resetAdSenseForTest(): void {
  script = null;
}
